import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  HardHat,
  Clock,
  Users,
  ArrowRight,
  Star,
  Clipboard,
  Sparkles
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const PostConstructionCleaningPage = () => {
  const services = [
    {
      title: "New Construction Final Cleaning",
      description: "Final cleanup for new builds before owner move-in or final walkthroughs. Remove construction dust, residue, and debris to reveal the finished space."
    },
    {
      title: "Renovation & Remodel Cleanup",
      description: "Post-renovation cleaning for kitchens, bathrooms, additions, and whole-home remodels. Detailed dust removal and final presentation."
    },
    {
      title: "Punch List Support",
      description: "Cleaning to support builder punch lists and final inspections. We work with your timeline to make sure spaces are clean for final approval."
    },
    {
      title: "Commercial Construction Cleanup",
      description: "Final cleanup for commercial renovations, tenant build-outs, and office remodels. Professional results for business spaces."
    }
  ];

  const whatWeClean = [
    "Construction dust removal (walls, ceilings, surfaces)",
    "Windows, frames, and sills",
    "Floors (sweep, vacuum, mop, residue removal)",
    "Cabinets and countertops (inside and out)",
    "Appliances and fixtures",
    "Baseboards, trim, and door frames",
    "Light fixtures and ceiling fans",
    "Final walk-through and touch-ups"
  ];

  const whyChoose = [
    "Experienced with post-construction cleaning",
    "Detailed dust removal and final presentation",
    "Coordination with builders and contractors",
    "Flexible scheduling around project timelines",
    "15+ years of professional cleaning experience",
    "Direct communication with Bob and Heather"
  ];

  const faqs = [
    {
      question: "What's included in post-construction cleaning?",
      answer: "Post-construction cleaning focuses on removing construction dust, debris, and residue from every surface—walls, ceilings, floors, windows, cabinets, fixtures, and trim. The goal is to make the space clean, presentable, and ready for occupancy or final walkthrough."
    },
    {
      question: "Do you work with builders and contractors?",
      answer: "Yes. We coordinate with builders, contractors, and project managers to schedule cleaning around final inspections, punch lists, and owner move-ins. We're flexible and understand construction timelines."
    },
    {
      question: "How long does post-construction cleaning take?",
      answer: "It depends on the size and scope of the project. A single-room renovation might take a few hours, while a full new construction home could take a full day or more. We'll provide an estimate based on your project."
    },
    {
      question: "Can you handle heavy construction dust?",
      answer: "Yes. Post-construction cleaning is more intensive than regular house cleaning. We're equipped to handle dust, drywall residue, sawdust, and construction debris. We'll make multiple passes if needed to get surfaces clean."
    },
    {
      question: "Do you provide punch list cleaning or touch-ups?",
      answer: "Absolutely. If punch list items require cleaning or if there are touch-ups needed before final inspection, we'll coordinate with you to make sure everything is addressed."
    },
    {
      question: "Can you clean commercial construction projects?",
      answer: "Yes. We handle post-construction cleaning for commercial spaces—tenant build-outs, office renovations, and retail remodels. We work around your business schedule and project timeline."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Post-Construction Cleaning Services in Brevard County | Pierce Premier",
      description: "Professional post-construction cleanup for new builds, renovations, and remodels in Brevard County. Detailed dust removal, final presentation. Call 321-408-1347.",
      url: "/services/commercial/construction-cleanup"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Commercial Cleaning", url: "/services/commercial" },
      { name: "Post-Construction Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Post-Construction Cleaning Services",
      description: "Professional post-construction cleanup for residential and commercial projects in Brevard County. New builds, renovations, remodels, and punch list support."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Post-Construction Cleaning Services in Brevard County | Pierce Premier"
        description="Professional post-construction cleanup for new builds, renovations, and remodels in Brevard County. Detailed dust removal, final presentation. Call 321-408-1347."
        canonical="/services/commercial/construction-cleanup"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <HardHat className="w-5 h-5 text-orange-600" />
                  <Badge className="bg-orange-600 text-white">Builder Partners</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Post-Construction Cleaning Services in Brevard County
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Builders, contractors, and homeowners need final cleanup that reveals the finished work—removing construction dust, debris, and residue so spaces are clean, presentable, and ready for occupancy or final walkthrough. Pierce Premier provides detailed post-construction cleaning for residential and commercial projects throughout Brevard County.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  We're Bob and Heather—a husband-and-wife team serving builders and contractors in Brevard County. We've handled post-construction cleanup for kitchen and bathroom remodels, whole-home renovations, and new construction projects. We coordinate with your timeline and work to help you close out projects on schedule.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    Request Project Estimate
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-orange-600 text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80" 
                  alt="Post-construction cleaning services for new builds and renovations in Brevard County, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Builders & Contractors Choose Pierce Premier
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Detailed Dust Removal</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Construction dust settles everywhere. We thoroughly clean walls, ceilings, surfaces, windows, and floors—making multiple passes if needed to remove dust and reveal the finished work.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Clipboard className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Punch List & Timeline Coordination</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We work with your project schedule—cleaning around inspections, punch lists, and owner walkthroughs. Flexible and responsive to keep your project moving forward.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Direct Communication</h3>
                  <p className="text-gray-600 leading-relaxed">
                    You'll work directly with Bob and Heather. Call or text to schedule, ask questions, or coordinate timing. No phone trees—just straightforward, responsive communication.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">15+ Years of Experience</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather brings over 15 years of executive housekeeping experience. We understand what final presentation looks like and deliver professional results for your projects.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  How Post-Construction Cleaning Works
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Project Assessment</h3>
                      <p className="text-gray-600">We visit the site or review project details, discuss the scope of work, and provide an estimate. We'll coordinate timing around your schedule and final inspection dates.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Detailed Cleaning</h3>
                      <p className="text-gray-600">We thoroughly clean all surfaces, removing construction dust, debris, and residue. Multiple passes if needed to make sure walls, floors, windows, and fixtures are spotless.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Final Walkthrough</h3>
                      <p className="text-gray-600">We do a final check and touch-ups to ensure the space is presentation-ready for your client, owner, or final inspection.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Post-Construction Services
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-orange-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                What We Clean After Construction
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whatWeClean.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-orange-100">
                  <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Builders Work With Us
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {whyChoose.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Service Areas
              </h2>
              <p className="text-gray-600">Serving builders and contractors throughout Brevard County</p>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "Palm Bay", link: "/areas/palm-bay" },
                { name: "Viera", link: "/areas/viera" },
                { name: "Rockledge", link: "/areas/rockledge" },
                { name: "Cocoa", link: "/areas/cocoa" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" }
              ].map((city, idx) => (
                <Link key={idx} to={city.link} className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200 hover:border-orange-400 transition-colors text-center group">
                  <span className="text-orange-700 font-semibold group-hover:text-orange-900">{city.name}</span>
                  <ArrowRight className="w-4 h-4 inline-block ml-1 text-orange-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions About Post-Construction Cleaning
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Related Services
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/services/deep-cleaning" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-orange-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600">Deep Cleaning Services</h3>
                <p className="text-gray-600 text-sm">Comprehensive deep cleaning for residential properties</p>
              </Link>
              <Link to="/services/commercial/property-management" className="p-6 bg-white rounded-lg border-2 border-gray-200 hover:border-orange-400 transition-colors group">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600">Property Management Cleaning</h3>
                <p className="text-gray-600 text-sm">Turnover and maintenance cleaning for rental properties</p>
              </Link>
            </div>
          </div>
        </section>

        <CTASection 
          title="Need Post-Construction Cleanup for Your Project?"
          description="Let's discuss your timeline and scope. We'll provide an honest estimate and coordinate with your schedule."
          primaryButtonText="Request Construction Cleanup Estimate"
        />
      </div>
    </>
  );
};

export default PostConstructionCleaningPage;
